import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { PageHeader } from "../../components/cards/PageHeader";
import { AppColors } from "../../constants/AppColors";
import { useCleanersScreen } from "./hooks/useCleanersScreen";
import AppLoadingOverlay from "../../components/loaders/AppLoadingOverlay";
import CleanerFormModal from "../../components/modals/CleanerFormModal";
import UpgradeRequiredModal from "../../components/modals/UpgradeRequiredModal";
import {
  getRoleTranslationKey,
  isCleaner,
  isManager,
  isViewer,
} from "../../utils/helperFunctions/roleHelper";
import { AuthUserResponse } from "../../models/ResponseModels/AuthUserResponse";

const CleanersScreen = () => {
  const vm = useCleanersScreen();

  return (
    <Box>
      <AppLoadingOverlay visible={vm.loading} />

      <PageHeader
        title={vm.t("cleaners.title")}
        description={vm.t("cleaners.description")}
        action={
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              rowGap: 1,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={vm.refresh}
              disabled={vm.loading}
            >
              {vm.t("common.refresh")}
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={vm.openCleanerModal}
              disabled={vm.loading}
              sx={{ borderRadius: 999, fontWeight: 900 }}
            >
              {vm.t("cleaners.add")}
            </Button>
          </Stack>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
          gap: 2,
          mb: 3,
        }}
      >
        <TeamStatCard
          label={vm.t("cleaners.cleaners")}
          value={vm.cleaners.length}
          icon={<GroupsOutlinedIcon />}
        />

        <TeamStatCard
          label={vm.t("roles.manager")}
          value={vm.managers.length}
          icon={<ShieldOutlinedIcon />}
        />

        <TeamStatCard
          label={vm.t("roles.cleaner")}
          value={vm.housekeepers.length}
          icon={<TaskAltOutlinedIcon />}
        />

        <TeamStatCard
          label={vm.t("roles.viewer")}
          value={vm.viewers.length}
          icon={<VisibilityOutlinedIcon />}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 360px" },
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${AppColors.border}`,
            bgcolor: AppColors.card,
            boxShadow: AppColors.shadowCard,
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 2.5 },
              "&:last-child": {
                pb: { xs: 2, sm: 2.5 },
              },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{
                mb: 2,
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: AppColors.primary,
                    fontWeight: 950,
                    textTransform: "uppercase",
                    fontSize: 12,
                    letterSpacing: 0.7,
                  }}
                >
                  {vm.t("cleaners.teamAccess")}
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: 950, mt: 0.3 }}>
                  {vm.t("cleaners.cleaners")}
                </Typography>
              </Box>

              <Chip
                size="small"
                label={`${vm.cleaners.length} ${vm.t("cleaners.members")}`}
                sx={{
                  bgcolor: AppColors.surfaceSoft,
                  border: `1px solid ${AppColors.border}`,
                  fontWeight: 900,
                }}
              />
            </Stack>

            {vm.cleaners.length === 0 ? (
              <Box
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: `1px dashed ${AppColors.border}`,
                  bgcolor: AppColors.surfaceSoft,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontWeight: 900 }}>
                  {vm.t("cleaners.noCleaners")}
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={vm.openCleanerModal}
                  disabled={vm.loading}
                  sx={{ mt: 2, borderRadius: 999, fontWeight: 900 }}
                >
                  {vm.t("cleaners.add")}
                </Button>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {vm.cleaners.map((member) => (
                  <TeamMemberRow
                    key={member.id}
                    member={member}
                    loading={vm.loading}
                    t={vm.t}
                    onRemove={() => vm.removeCleaner(member)}
                  />
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Stack spacing={2}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: `1px solid ${AppColors.border}`,
              bgcolor: AppColors.card,
              boxShadow: AppColors.shadowCard,
            }}
          >
            <CardContent
              sx={{
                p: { xs: 2, sm: 2.5 },
                "&:last-child": {
                  pb: { xs: 2, sm: 2.5 },
                },
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center" }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "16px",
                    bgcolor: AppColors.surfaceBlue,
                    color: AppColors.primary,
                    border: `1px solid ${AppColors.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AddIcon />
                </Box>

                <Box>
                  <Typography sx={{ fontWeight: 950 }}>
                    {vm.t("cleaners.newCleaner")}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {vm.t("cleaners.addMemberDescription")}
                  </Typography>
                </Box>
              </Stack>

              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={vm.openCleanerModal}
                disabled={vm.loading}
                sx={{ mt: 2, borderRadius: 999, fontWeight: 900 }}
              >
                {vm.t("cleaners.add")}
              </Button>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: `1px solid ${AppColors.border}`,
              bgcolor: AppColors.card,
              boxShadow: AppColors.shadowCard,
            }}
          >
            <CardContent
              sx={{
                p: { xs: 2, sm: 2.5 },
                "&:last-child": {
                  pb: { xs: 2, sm: 2.5 },
                },
              }}
            >
              <Typography sx={{ fontWeight: 950, mb: 1 }}>
                {vm.t("cleaners.roleOverview")}
              </Typography>

              <Stack spacing={1}>
                <RoleInfoChip
                  label={vm.t("roles.manager")}
                  description={vm.t("cleaners.managerDescription")}
                />

                <RoleInfoChip
                  label={vm.t("roles.viewer")}
                  description={vm.t("cleaners.viewerDescription")}
                />

                <RoleInfoChip
                  label={vm.t("roles.cleaner")}
                  description={vm.t("cleaners.cleanerDescription")}
                />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <CleanerFormModal
        open={vm.cleanerModalOpen}
        loading={vm.loading}
        onClose={vm.closeCleanerModal}
        onSubmit={vm.submitCleaner}
      />

      <UpgradeRequiredModal
        open={vm.upgradeModalOpen}
        title={vm.t("subscription.upgradeRequired")}
        message={vm.upgradeMessage}
        onClose={vm.closeUpgradeModal}
      />
    </Box>
  );
};

const TeamMemberRow = ({
  member,
  loading,
  t,
  onRemove,
}: {
  member: AuthUserResponse;
  loading: boolean;
  t: (key: string, values?: Record<string, string | number>) => string;
  onRemove: () => void;
}) => {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 3,
        border: `1px solid ${AppColors.border}`,
        bgcolor: AppColors.surface,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "16px",
              bgcolor: AppColors.surfaceBlue,
              color: AppColors.primary,
              border: `1px solid ${AppColors.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 950,
              flexShrink: 0,
            }}
          >
            {getInitials(member.name)}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.75 }}
            >
              <Typography sx={{ fontWeight: 950, wordBreak: "break-word" }}>
                {member.name}
              </Typography>

              <RoleChip member={member} t={t} />

              {!member.email_verified ? (
                <Chip
                  size="small"
                  label={t("auth.verifyEmail")}
                  variant="outlined"
                  sx={{ fontWeight: 850 }}
                />
              ) : null}
            </Stack>

            <Stack
              direction="row"
              spacing={1.5}
              sx={{ mt: 0.65, flexWrap: "wrap", rowGap: 0.5 }}
            >
              <SmallContactLine
                icon={<EmailOutlinedIcon />}
                text={member.email}
              />

              {member.phone ? (
                <SmallContactLine
                  icon={<PhoneOutlinedIcon />}
                  text={member.phone}
                />
              ) : null}
            </Stack>
          </Box>
        </Stack>

        <Tooltip title={t("common.delete")}>
          <IconButton
            size="small"
            color="error"
            onClick={onRemove}
            disabled={loading}
            sx={{
              border: `1px solid ${AppColors.border}`,
              alignSelf: { xs: "flex-end", sm: "center" },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

const TeamStatCard = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactElement;
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${AppColors.border}`,
        bgcolor: AppColors.card,
        height: "100%",
        boxShadow: "0 8px 22px rgba(15, 23, 42, 0.04)",
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          "&:last-child": {
            pb: 2.5,
          },
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "14px",
              bgcolor: AppColors.surfaceBlue,
              color: AppColors.primary,
              border: `1px solid ${AppColors.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 800 }}
            >
              {label}
            </Typography>

            <Typography variant="h5" sx={{ mt: 0.25, fontWeight: 950 }}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const SmallContactLine = ({
  icon,
  text,
}: {
  icon: React.ReactElement;
  text: string;
}) => {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      {icon}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ wordBreak: "break-word" }}
      >
        {text}
      </Typography>
    </Stack>
  );
};

const RoleChip = ({
  member,
  t,
}: {
  member: AuthUserResponse;
  t: (key: string, values?: Record<string, string | number>) => string;
}) => {
  const palette = getRolePalette(member);

  return (
    <Chip
      size="small"
      label={t(getRoleTranslationKey(member.role))}
      sx={{
        fontWeight: 900,
        bgcolor: palette.background,
        color: palette.color,
        border: `1px solid ${palette.border}`,
      }}
    />
  );
};

const RoleInfoChip = ({
  label,
  description,
}: {
  label: string;
  description: string;
}) => {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 3,
        border: `1px solid ${AppColors.border}`,
        bgcolor: AppColors.surface,
      }}
    >
      <Typography sx={{ fontWeight: 900 }}>{label}</Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
        {description}
      </Typography>
    </Box>
  );
};

const getInitials = (name: string) => {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const getRolePalette = (member: AuthUserResponse) => {
  if (isManager(member.role)) {
    return {
      background: "#dcfce7",
      color: "#15803d",
      border: "#bbf7d0",
    };
  }

  if (isCleaner(member.role)) {
    return {
      background: "#ffedd5",
      color: "#c2410c",
      border: "#fed7aa",
    };
  }

  if (isViewer(member.role)) {
    return {
      background: "#fef3c7",
      color: "#a16207",
      border: "#fde68a",
    };
  }

  return {
    background: "#eff6ff",
    color: "#2563eb",
    border: "#bfdbfe",
  };
};

export default CleanersScreen;
